import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Loader2, Check, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useBranding, buildBrandingUrl } from '../contexts/BrandingContext';
import { REG_STORAGE_KEY } from './RegisterPage';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const BG_IMAGE = 'https://customer-assets.emergentagent.com/job_4dc508e2-00ad-4791-a42f-c0685c15cd50/artifacts/w7lm448x_background.png';

const LIFESTYLES = [
  { value: 'lazy', label: 'Lazy' },
  { value: 'lightly_active', label: 'Lightly active' },
  { value: 'moderate_active', label: 'Moderate active' },
  { value: 'very_active', label: 'Very active' },
  { value: 'super_active', label: 'Super active' },
];

const GOALS = [
  { value: 'losing_weight', label: 'Losing weight' },
  { value: 'maintaining_weight', label: 'Maintaining weight' },
  { value: 'gaining_weight', label: 'Gaining weight' },
  { value: 'improve_fitness', label: 'Improve fitness' },
  { value: 'better_nutrition', label: 'Better nutrition' },
  { value: 'customized_goals', label: 'Customized goals' },
];

const HEALTH_CONCERNS = ['Diabetes', 'Blood Pressure', 'Cholesterol', 'Digestive', 'Acid Reflux', 'Other'];
const FOOD_ALLERGIES = ['Nuts', 'Eggs', 'Dairy Items', 'Wheat', 'Seafood', 'Other'];

const ACTIVITY = { lazy: 1.2, lightly_active: 1.375, moderate_active: 1.55, very_active: 1.725, super_active: 1.9 };

// ── Harris-Benedict nutrition calc ───────────────────────────────────────────
function calcNutrition({ gender, age, weightKg, heightCm, lifestyle, goal }) {
  const w = weightKg || 70, h = heightCm || 170, a = age || 30;
  const bmr = gender === 'female'
    ? 447.6 + (9.25 * w) + (3.10 * h) - (4.33 * a)
    : 88.36 + (13.4 * w) + (4.8 * h) - (5.68 * a);
  const mult = ACTIVITY[lifestyle] || 1.55;
  const adj = goal === 'losing_weight' ? -500 : goal === 'gaining_weight' ? 300 : 0;
  const calories = Math.round(bmr * mult + adj);
  return {
    calories,
    protein: Math.round(calories * 0.25 / 4),
    carbs: Math.round(calories * 0.45 / 4),
    fat: Math.round(calories * 0.30 / 9),
    water: parseFloat((w * 0.035).toFixed(1)),
  };
}

// ── Drum Picker ───────────────────────────────────────────────────────────────
function DrumPicker({ values, value, onChange }) {
  const idx = values.indexOf(value);
  const safeIdx = idx < 0 ? 0 : idx;
  const ITEM_H = 50;
  const VISIBLE = 5;
  const CENTER = 2;

  const dec = () => safeIdx > 0 && onChange(values[safeIdx - 1]);
  const inc = () => safeIdx < values.length - 1 && onChange(values[safeIdx + 1]);

  const handleWheel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.deltaY > 0) inc();
    else dec();
  };

  const visible = [];
  for (let i = safeIdx - CENTER; i <= safeIdx + CENTER; i++) {
    visible.push(i >= 0 && i < values.length ? values[i] : null);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', userSelect: 'none', width: 100 }}>
      <button onClick={dec} disabled={safeIdx === 0} style={arrowBtn}>
        <ChevronUp size={20} color={safeIdx === 0 ? '#d1d5db' : '#374151'} />
      </button>
      <div onWheel={handleWheel} style={{ height: ITEM_H * VISIBLE, overflow: 'hidden', position: 'relative', width: '100%' }}>
        {visible.map((v, i) => {
          const isSel = i === CENTER;
          const dist = Math.abs(i - CENTER);
          return (
            <div
              key={i}
              onClick={() => v !== null && onChange(v)}
              style={{
                height: ITEM_H, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: isSel ? 28 : dist === 1 ? 20 : 16,
                fontWeight: isSel ? 700 : 400,
                color: isSel ? '#fff' : dist === 1 ? '#6b7280' : '#d1d5db',
                background: isSel ? '#f97316' : 'transparent',
                borderRadius: isSel ? 12 : 0,
                cursor: v !== null ? 'pointer' : 'default',
                opacity: v !== null ? 1 : 0,
                transition: 'background 0.15s',
              }}
            >
              {v !== null ? v : ''}
            </div>
          );
        })}
      </div>
      <button onClick={inc} disabled={safeIdx === values.length - 1} style={arrowBtn}>
        <ChevronDown size={20} color={safeIdx === values.length - 1 ? '#d1d5db' : '#374151'} />
      </button>
    </div>
  );
}

const arrowBtn = {
  background: 'none', border: 'none', cursor: 'pointer',
  padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
};

// ── Unit Toggle ───────────────────────────────────────────────────────────────
function UnitToggle({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: 999, padding: 3, marginBottom: 20 }}>
      {options.map(opt => (
        <button
          key={opt}
          onClick={() => onChange(opt)}
          style={{
            padding: '8px 24px', borderRadius: 999, border: 'none', cursor: 'pointer', fontSize: 14,
            fontWeight: 600, transition: 'all 0.2s',
            background: value === opt ? '#1a2744' : 'transparent',
            color: value === opt ? '#fff' : '#6b7280',
          }}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function RegisterSurveyPage() {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const logoSrc = buildBrandingUrl(branding?.app_logo || branding?.logo_url);

  const [regData, setRegData] = useState(null);
  const [step, setStep] = useState(1);
  const [activeModal, setActiveModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Wizard data
  const [gender, setGender] = useState('');
  const [age, setAge] = useState(25);
  const [heightUnit, setHeightUnit] = useState('cm');
  const [heightCm, setHeightCm] = useState(170);
  const [heightFt, setHeightFt] = useState(5);
  const [heightIn, setHeightIn] = useState(7);
  const [weightUnit, setWeightUnit] = useState('kg');
  const [weightInt, setWeightInt] = useState(70);
  const [weightDec, setWeightDec] = useState(0);
  const [targetInt, setTargetInt] = useState(65);
  const [targetDec, setTargetDec] = useState(0);
  const [lifestyle, setLifestyle] = useState('');
  const [goal, setGoal] = useState('');
  const [healthConcerns, setHealthConcerns] = useState([]);
  const [foodAllergies, setFoodAllergies] = useState([]);

  // Temp modal state (committed on Submit)
  const [tmpHeightUnit, setTmpHeightUnit] = useState('cm');
  const [tmpHeightCm, setTmpHeightCm] = useState(170);
  const [tmpHeightFt, setTmpHeightFt] = useState(5);
  const [tmpHeightIn, setTmpHeightIn] = useState(7);
  const [tmpWeightUnit, setTmpWeightUnit] = useState('kg');
  const [tmpWeightInt, setTmpWeightInt] = useState(70);
  const [tmpWeightDec, setTmpWeightDec] = useState(0);
  const [tmpTargetInt, setTmpTargetInt] = useState(65);
  const [tmpTargetDec, setTmpTargetDec] = useState(0);
  const [tmpAge, setTmpAge] = useState(25);
  const [tmpGender, setTmpGender] = useState('');

  useEffect(() => {
    const stored = localStorage.getItem(REG_STORAGE_KEY);
    if (!stored) { navigate('/register', { replace: true }); return; }
    setRegData(JSON.parse(stored));
    setTimeout(() => setMounted(true), 60);
  }, [navigate]);

  // ── Computed display values ──────────────────────────────────────────────
  const heightDisplay = () => {
    if (!gender && !age) return null;
    if (heightUnit === 'cm') return heightCm ? `${heightCm} cm` : null;
    return (heightFt || heightIn !== undefined) ? `${heightFt}' ${heightIn}"` : null;
  };
  const weightDisplay = () => weightInt ? `${weightInt}.${weightDec} ${weightUnit}` : null;
  const targetDisplay = () => targetInt ? `${targetInt}.${targetDec} ${weightUnit}` : null;

  const step1Complete = gender && age && (heightUnit === 'cm' ? heightCm : (heightFt)) && weightInt && targetInt;

  // ── Modal openers ─────────────────────────────────────────────────────────
  const openModal = (which) => {
    if (which === 'height') { setTmpHeightUnit(heightUnit); setTmpHeightCm(heightCm); setTmpHeightFt(heightFt); setTmpHeightIn(heightIn); }
    if (which === 'weight') { setTmpWeightUnit(weightUnit); setTmpWeightInt(weightInt); setTmpWeightDec(weightDec); }
    if (which === 'targetWeight') { setTmpTargetInt(targetInt); setTmpTargetDec(targetDec); }
    if (which === 'age') setTmpAge(age);
    if (which === 'gender') setTmpGender(gender);
    setActiveModal(which);
  };

  // ── Modal submitters ──────────────────────────────────────────────────────
  const submitModal = () => {
    if (activeModal === 'height') { setHeightUnit(tmpHeightUnit); setHeightCm(tmpHeightCm); setHeightFt(tmpHeightFt); setHeightIn(tmpHeightIn); }
    if (activeModal === 'weight') { setWeightUnit(tmpWeightUnit); setWeightInt(tmpWeightInt); setWeightDec(tmpWeightDec); setTargetInt(tmpTargetInt); setTargetDec(tmpTargetDec); }
    if (activeModal === 'targetWeight') { setTargetInt(tmpTargetInt); setTargetDec(tmpTargetDec); }
    if (activeModal === 'age') setAge(tmpAge);
    if (activeModal === 'gender') setGender(tmpGender);
    setActiveModal(null);
  };

  // ── Toggle multi-select ───────────────────────────────────────────────────
  const toggle = (arr, setArr, val) =>
    setArr(a => a.includes(val) ? a.filter(v => v !== val) : [...a, val]);

  // ── Final submit ──────────────────────────────────────────────────────────
  const handleFinish = async () => {
    setLoading(true);
    try {
      const { surveyId, profileToken, refCode, userId } = regData;
      const today = new Date().toISOString();

      // Convert height to cm
      const finalHeightCm = heightUnit === 'cm' ? heightCm : Math.round((heightFt * 12 + heightIn) * 2.54);
      const finalWeightKg = weightUnit === 'kg' ? weightInt + weightDec / 10 : parseFloat(((weightInt + weightDec / 10) * 0.453592).toFixed(1));
      const finalTargetKg = weightUnit === 'kg' ? targetInt + targetDec / 10 : parseFloat(((targetInt + targetDec / 10) * 0.453592).toFixed(1));

      const surveyPayload = {
        gender,
        age,
        height_cm: finalHeightCm,
        weight_kg: finalWeightKg,
        target_weight: finalTargetKg,
        height_unit: heightUnit,
        weight_unit: weightUnit,
        lifestyle,
        goal,
        health_issues: healthConcerns.join(', '),
        food_allergies: foodAllergies.join(', '),
        date: today,
      };

      const authHeader = profileToken ? { Authorization: `Bearer ${profileToken}` } : {};

      // 1. Update Survey
      if (surveyId) {
        await axios.put(
          `${API}/api/v1/mobile/auth/updateSurvey/${surveyId}`,
          surveyPayload,
          { headers: authHeader }
        );
      }

      // 2. Update Moderate Relax (nutrition calculation)
      const nutrition = calcNutrition({ gender, age, weightKg: finalWeightKg, heightCm: finalHeightCm, lifestyle, goal });
      await axios.post(
        `${API}/api/v1/mobile/auth/updateModerateRelax`,
        { ...nutrition, date: today },
        { headers: authHeader }
      );

      // 3. Apply referral if present
      if (refCode && userId) {
        try {
          await axios.post(`${API}/api/v1/mobile/auth/updateReferal`, { code: refCode, user_id: userId });
        } catch { /* non-critical */ }
      }

      localStorage.removeItem(REG_STORAGE_KEY);
      toast.success('Profile complete! Welcome to WingsDiet.');
      navigate('/download');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!regData) return null;

  const ages = Array.from({ length: 71 }, (_, i) => i + 10);
  const cmValues = Array.from({ length: 121 }, (_, i) => i + 100);
  const ftValues = Array.from({ length: 5 }, (_, i) => i + 3);
  const inValues = Array.from({ length: 12 }, (_, i) => i);
  const kgValues = Array.from({ length: 171 }, (_, i) => i + 30);
  const lbValues = Array.from({ length: 375 }, (_, i) => i + 66);
  const decValues = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

  return (
    <div
      data-testid="survey-page"
      style={{
        minHeight: '100vh',
        backgroundImage: `url(${BG_IMAGE})`,
        backgroundSize: 'cover', backgroundPosition: 'center',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative', padding: '24px 16px',
        fontFamily: "'Nunito', 'Inter', sans-serif",
      }}
    >
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(8,8,14,0.3)', pointerEvents: 'none' }} />

      {/* Card */}
      <div
        data-testid="survey-card"
        style={{
          position: 'relative', zIndex: 1, width: '100%', maxWidth: 440,
          background: 'rgba(255,255,255,0.96)',
          backdropFilter: 'blur(28px)', WebkitBackdropFilter: 'blur(28px)',
          borderRadius: 24, padding: '32px 28px 28px',
          boxShadow: '0 8px 48px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,1)',
          border: '1px solid rgba(255,255,255,0.65)',
          opacity: mounted ? 1 : 0, transform: mounted ? 'translateY(0)' : 'translateY(16px)',
          transition: 'opacity 0.5s ease, transform 0.5s ease',
          minHeight: 480,
        }}
      >
        {/* Logo */}
        {logoSrc && (
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <img src={logoSrc} alt="Logo" style={{ height: 36, maxWidth: 120, objectFit: 'contain' }} />
          </div>
        )}

        {/* Progress Bar */}
        <ProgressBar step={step} />

        {/* Step content */}
        <div style={{ marginTop: 24 }}>
          {step === 1 && (
            <Step1Personal
              gender={gender} age={age}
              heightDisplay={heightDisplay()} weightDisplay={weightDisplay()} targetDisplay={targetDisplay()}
              openModal={openModal}
            />
          )}
          {step === 2 && <Step2Lifestyle selected={lifestyle} onSelect={setLifestyle} />}
          {step === 3 && <Step3Goal selected={goal} onSelect={setGoal} />}
          {step === 4 && (
            <Step4Health
              healthConcerns={healthConcerns} foodAllergies={foodAllergies}
              toggle={toggle}
              setHealthConcerns={setHealthConcerns}
              setFoodAllergies={setFoodAllergies}
            />
          )}
        </div>

        {/* Nav buttons */}
        <div style={{ display: 'flex', gap: 10, marginTop: 28 }}>
          {step > 1 && (
            <button
              onClick={() => setStep(s => s - 1)}
              style={backBtnStyle}
              data-testid="wizard-back-btn"
            >
              Back
            </button>
          )}
          {step < 4 ? (
            <button
              onClick={() => setStep(s => s + 1)}
              disabled={step === 1 && !step1Complete || step === 2 && !lifestyle || step === 3 && !goal}
              data-testid="wizard-next-btn"
              style={{
                ...nextBtnStyle, flex: 1,
                opacity: (step === 1 && !step1Complete) || (step === 2 && !lifestyle) || (step === 3 && !goal) ? 0.45 : 1,
                cursor: (step === 1 && !step1Complete) || (step === 2 && !lifestyle) || (step === 3 && !goal) ? 'not-allowed' : 'pointer',
              }}
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleFinish}
              disabled={loading}
              data-testid="wizard-finish-btn"
              style={{ ...nextBtnStyle, flex: 1, opacity: loading ? 0.7 : 1 }}
            >
              {loading ? <><Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> Saving...</> : 'Finish'}
            </button>
          )}
        </div>
      </div>

      {/* ── Modals ── */}
      {activeModal && (
        <div
          onClick={() => setActiveModal(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            zIndex: 100, padding: '0 16px 20px',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 20, padding: '24px 24px 20px',
              width: '100%', maxWidth: 440,
              boxShadow: '0 -8px 40px rgba(0,0,0,0.2)',
            }}
          >
            {/* Gender Modal */}
            {activeModal === 'gender' && (
              <div>
                <h3 style={modalTitle}>Select Gender</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                  {['Male', 'Female'].map(g => (
                    <button
                      key={g}
                      data-testid={`gender-${g.toLowerCase()}`}
                      onClick={() => setTmpGender(g.toLowerCase())}
                      style={{
                        padding: '14px 20px', borderRadius: 12, border: `2px solid ${tmpGender === g.toLowerCase() ? '#f97316' : '#e5e7eb'}`,
                        background: tmpGender === g.toLowerCase() ? '#fff7ed' : '#fafafa',
                        color: '#1a1a2e', fontWeight: 600, fontSize: 15, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      }}
                    >
                      {g}
                      <div style={{
                        width: 20, height: 20, borderRadius: '50%',
                        border: `2px solid ${tmpGender === g.toLowerCase() ? '#f97316' : '#d1d5db'}`,
                        background: tmpGender === g.toLowerCase() ? '#f97316' : 'transparent',
                      }} />
                    </button>
                  ))}
                </div>
                <button onClick={submitModal} disabled={!tmpGender} style={{ ...nextBtnStyle, width: '100%', opacity: tmpGender ? 1 : 0.5 }}>Submit</button>
              </div>
            )}

            {/* Age Modal */}
            {activeModal === 'age' && (
              <div>
                <h3 style={modalTitle}>Your Age</h3>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12 }}>
                  <DrumPicker values={ages} value={tmpAge} onChange={setTmpAge} />
                  <span style={{ fontSize: 18, fontWeight: 700, color: '#6b7280', marginTop: 0 }}>Years</span>
                </div>
                <button onClick={submitModal} style={{ ...nextBtnStyle, width: '100%', marginTop: 20 }}>Submit</button>
              </div>
            )}

            {/* Height Modal */}
            {activeModal === 'height' && (
              <div>
                <h3 style={modalTitle}>Your Height</h3>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <UnitToggle options={['feet', 'cm']} value={tmpHeightUnit} onChange={setTmpHeightUnit} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, marginBottom: 8 }}>
                  {tmpHeightUnit === 'cm' ? (
                    <>
                      <DrumPicker values={cmValues} value={tmpHeightCm} onChange={setTmpHeightCm} />
                      <span style={{ fontSize: 18, fontWeight: 700, color: '#6b7280' }}>CM</span>
                    </>
                  ) : (
                    <>
                      <DrumPicker values={ftValues} value={tmpHeightFt} onChange={setTmpHeightFt} />
                      <span style={{ fontSize: 22, fontWeight: 700, color: '#374151' }}>'</span>
                      <DrumPicker values={inValues} value={tmpHeightIn} onChange={setTmpHeightIn} />
                      <span style={{ fontSize: 22, fontWeight: 700, color: '#374151' }}>"</span>
                    </>
                  )}
                </div>
                <button onClick={submitModal} style={{ ...nextBtnStyle, width: '100%', marginTop: 12 }}>Submit</button>
              </div>
            )}

            {/* Weight Modal */}
            {activeModal === 'weight' && (
              <div>
                <h3 style={modalTitle}>Your Weight</h3>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <UnitToggle options={['lb', 'kg']} value={tmpWeightUnit} onChange={setTmpWeightUnit} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8 }}>
                  <DrumPicker values={tmpWeightUnit === 'kg' ? kgValues : lbValues} value={tmpWeightInt} onChange={setTmpWeightInt} />
                  <span style={{ fontSize: 24, fontWeight: 700, color: '#374151' }}>.</span>
                  <DrumPicker values={decValues} value={tmpWeightDec} onChange={setTmpWeightDec} />
                  <span style={{ fontSize: 18, fontWeight: 700, color: '#6b7280' }}>{tmpWeightUnit.toUpperCase()}</span>
                </div>
                <button onClick={submitModal} style={{ ...nextBtnStyle, width: '100%', marginTop: 16 }}>Submit</button>
              </div>
            )}

            {/* Target Weight Modal */}
            {activeModal === 'targetWeight' && (
              <div>
                <h3 style={modalTitle}>Target Weight</h3>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8 }}>
                  <DrumPicker values={weightUnit === 'kg' ? kgValues : lbValues} value={tmpTargetInt} onChange={setTmpTargetInt} />
                  <span style={{ fontSize: 24, fontWeight: 700, color: '#374151' }}>.</span>
                  <DrumPicker values={decValues} value={tmpTargetDec} onChange={setTmpTargetDec} />
                  <span style={{ fontSize: 18, fontWeight: 700, color: '#6b7280' }}>{weightUnit.toUpperCase()}</span>
                </div>
                <button onClick={submitModal} style={{ ...nextBtnStyle, width: '100%', marginTop: 16 }}>Submit</button>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
}

// ── Progress Bar ──────────────────────────────────────────────────────────────
function ProgressBar({ step }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      {[1, 2, 3, 4].map(n => (
        <div key={n} style={{ flex: 1, textAlign: 'center' }}>
          <div style={{
            fontSize: 15, fontWeight: 700,
            color: step >= n ? '#1e3a8a' : '#9ca3af',
            marginBottom: 4,
          }}>{n}</div>
          <div style={{
            height: 3, borderRadius: 999,
            background: step > n ? '#1e3a8a' : step === n ? '#f97316' : '#e5e7eb',
            transition: 'background 0.3s',
          }} />
        </div>
      ))}
    </div>
  );
}

// ── Step 1: Personal Details ──────────────────────────────────────────────────
function Step1Personal({ gender, age, heightDisplay, weightDisplay, targetDisplay, openModal }) {
  const rows = [
    { key: 'gender', icon: '👤', label: 'Gender', value: gender ? (gender.charAt(0).toUpperCase() + gender.slice(1)) : null },
    { key: 'age', icon: '⏳', label: 'Age', value: age ? `${age} Years` : null },
    { key: 'height', icon: '↕️', label: 'Height', value: heightDisplay },
    { key: 'weight', icon: '⚖️', label: 'Weight', value: weightDisplay },
    { key: 'targetWeight', icon: '🎯', label: 'Target Weight', value: targetDisplay },
  ];

  return (
    <div>
      <h2 style={stepTitle}>Enter your personal Details</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 16 }}>
        {rows.map((row, i) => (
          <button
            key={row.key}
            data-testid={`field-${row.key}`}
            onClick={() => openModal(row.key)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 16px',
              background: '#fafafa',
              border: '1.5px solid #f3f4f6',
              borderRadius: 12,
              cursor: 'pointer',
              transition: 'border-color 0.2s',
              marginBottom: 8,
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#f97316'}
            onMouseLeave={e => e.currentTarget.style.borderColor = '#f3f4f6'}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 20 }}>{row.icon}</span>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {row.label}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: row.value ? '#1a1a2e' : '#9ca3af', marginTop: 1 }}>
                  {row.value || 'Tap to select'}
                </div>
              </div>
            </div>
            <ChevronRight size={18} color="#9ca3af" />
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Step 2: Lifestyle ─────────────────────────────────────────────────────────
function Step2Lifestyle({ selected, onSelect }) {
  return (
    <div>
      <h2 style={stepTitle}>What's your life style?</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
        {LIFESTYLES.map(({ value, label }) => {
          const active = selected === value;
          return (
            <button
              key={value}
              data-testid={`lifestyle-${value}`}
              onClick={() => onSelect(value)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 16px',
                border: `2px solid ${active ? '#f97316' : '#e5e7eb'}`,
                borderRadius: 12,
                background: active ? '#fff7ed' : '#fafafa',
                cursor: 'pointer', transition: 'all 0.15s',
              }}
            >
              <span style={{ fontSize: 15, fontWeight: 600, color: '#1a1a2e' }}>{label}</span>
              <div style={{
                width: 22, height: 22, borderRadius: '50%',
                border: `2.5px solid ${active ? '#f97316' : '#d1d5db'}`,
                background: active ? '#f97316' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s',
              }}>
                {active && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Step 3: Goal ──────────────────────────────────────────────────────────────
function Step3Goal({ selected, onSelect }) {
  return (
    <div>
      <h2 style={stepTitle}>What's your goal?</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
        {GOALS.map(({ value, label }) => {
          const active = selected === value;
          return (
            <button
              key={value}
              data-testid={`goal-${value}`}
              onClick={() => onSelect(value)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 16px',
                border: `2px solid ${active ? '#f97316' : '#e5e7eb'}`,
                borderRadius: 12,
                background: active ? '#fff7ed' : '#fafafa',
                cursor: 'pointer', transition: 'all 0.15s',
              }}
            >
              <span style={{ fontSize: 15, fontWeight: 600, color: '#1a1a2e' }}>{label}</span>
              <div style={{
                width: 22, height: 22, borderRadius: '50%',
                border: `2.5px solid ${active ? '#f97316' : '#d1d5db'}`,
                background: active ? '#f97316' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s',
              }}>
                {active && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Step 4: Health & Allergies ────────────────────────────────────────────────
function Step4Health({ healthConcerns, foodAllergies, setHealthConcerns, setFoodAllergies, toggle }) {
  return (
    <div>
      <h2 style={stepTitle}>Health Issues &amp; Allergies</h2>
      <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 16 }}>Select all that apply (optional)</p>

      <div style={{ marginBottom: 20 }}>
        <p style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
          Health Concerns
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {HEALTH_CONCERNS.map(item => {
            const active = healthConcerns.includes(item);
            return (
              <button
                key={item}
                data-testid={`health-${item}`}
                onClick={() => toggle(healthConcerns, setHealthConcerns, item)}
                style={{
                  padding: '8px 16px', borderRadius: 999, border: `2px solid ${active ? '#f97316' : '#e5e7eb'}`,
                  background: active ? '#fff7ed' : '#fafafa',
                  color: active ? '#ea580c' : '#374151',
                  fontWeight: 600, fontSize: 13, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.15s',
                }}
              >
                {active && <Check size={12} strokeWidth={3} />}
                {item}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
          Food Allergies
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {FOOD_ALLERGIES.map(item => {
            const active = foodAllergies.includes(item);
            return (
              <button
                key={item}
                data-testid={`allergy-${item}`}
                onClick={() => toggle(foodAllergies, setFoodAllergies, item)}
                style={{
                  padding: '8px 16px', borderRadius: 999, border: `2px solid ${active ? '#f97316' : '#e5e7eb'}`,
                  background: active ? '#fff7ed' : '#fafafa',
                  color: active ? '#ea580c' : '#374151',
                  fontWeight: 600, fontSize: 13, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.15s',
                }}
              >
                {active && <Check size={12} strokeWidth={3} />}
                {item}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────────
const stepTitle = { color: '#1e3a8a', fontSize: 20, fontWeight: 800, margin: 0, letterSpacing: '-0.3px' };
const modalTitle = { color: '#1e3a8a', fontSize: 20, fontWeight: 800, textAlign: 'center', marginBottom: 20 };
const nextBtnStyle = {
  padding: '14px 0', background: '#1a2744', color: '#fff', border: 'none',
  borderRadius: 12, fontSize: 14, fontWeight: 800, letterSpacing: '0.5px',
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  gap: 8, boxShadow: '0 4px 16px rgba(26,39,68,0.3)', transition: 'background 0.2s',
};
const backBtnStyle = {
  padding: '13px 24px', background: '#f3f4f6', color: '#374151', border: '1.5px solid #e5e7eb',
  borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: 'pointer', transition: 'background 0.15s',
};
